import React from 'react';
import { Route, RedirectException } from 'found';
import { graphql } from 'react-relay';

import { DateTime } from 'luxon';
import Error404 from '../component/404';
import Loading from '../component/LoadingPage';
import {
  PREFIX_STOPS,
  PREFIX_TERMINALS,
  PREFIX_DISRUPTION,
  PREFIX_TIMETABLE,
} from '../../utils/shared/path';
import {
  getDefault,
  errorLoading,
  getComponentOrNullRenderer,
  createStickyRenderer,
  getComponentOrLoadingRenderer,
} from './routerUtils';
import {
  prepareDatesForStops,
  prepareDatesForStopTabs,
} from '../../utils/client/dateParamUtils';
import { DATE_FORMAT } from '../../utils/shared/constants';

const queries = {
  stop: {
    pageHeader: graphql`
      query stopRoutes_StopPageHeaderContainer_Query($stopId: String!) {
        stop(id: $stopId) {
          ...StopPageHeaderContainer_stop
        }
      }
    `,
    pageMap: graphql`
      query stopRoutes_StopPageMapContainer_Query($stopId: String!) {
        stop(id: $stopId) {
          ...StopPageMapContainer_stop
        }
      }
    `,
    pageMeta: graphql`
      query stopRoutes_StopPageMeta_Query($stopId: String!) {
        stop(id: $stopId) {
          ...StopPageMeta_stop
        }
      }
    `,
    pageTab: graphql`
      query stopRoutes_StopPageTab_Query(
        $stopId: String!
        $cancelationStartDate: OffsetDateTime!
        $cancelationEndDate: OffsetDateTime!
      ) {
        stop(id: $stopId) {
          ...StopPageTabContainer_stop
            @arguments(
              cancelationStartDate: $cancelationStartDate
              cancelationEndDate: $cancelationEndDate
            )
        }
      }
    `,
    pageContent: graphql`
      query stopRoutes_StopPageContent_Query($stopId: String!) {
        stop(id: $stopId) {
          ...StopPageContentContainer_stop
        }
      }
    `,
    pageTimetable: graphql`
      query stopRoutes_StopPageTimetable_Query(
        $stopId: String!
        $date: String!
      ) {
        stop(id: $stopId) {
          ...StopTimetablePage_stop @arguments(date: $date)
        }
      }
    `,
    pageAlerts: graphql`
      query stopRoutes_StopDisruptions_Query(
        $stopId: String!
        $cancelationStartDate: OffsetDateTime!
        $cancelationEndDate: OffsetDateTime!
      ) {
        stop(id: $stopId) {
          ...DisruptionsFragment
            @arguments(
              cancelationStartDate: $cancelationStartDate
              cancelationEndDate: $cancelationEndDate
            )
        }
      }
    `,
  },
  station: {
    pageHeader: graphql`
      query stopRoutes_TerminalPageHeaderContainer_Query($terminalId: String!) {
        station(id: $terminalId) {
          ...TerminalPageHeaderContainer_station
        }
      }
    `,
    pageMap: graphql`
      query stopRoutes_TerminalPageMapContainer_Query($terminalId: String!) {
        station(id: $terminalId) {
          ...TerminalPageMapContainer_station
        }
      }
    `,
    pageMeta: graphql`
      query stopRoutes_TerminalPageMeta_Query($terminalId: String!) {
        station(id: $terminalId) {
          ...TerminalPageMeta_station
        }
      }
    `,
    pageTab: graphql`
      query stopRoutes_TerminalPageTabContainer_Query(
        $terminalId: String!
        $cancelationStartDate: OffsetDateTime!
        $cancelationEndDate: OffsetDateTime!
      ) {
        station(id: $terminalId) {
          ...TerminalPageTabContainer_station
            @arguments(
              cancelationStartDate: $cancelationStartDate
              cancelationEndDate: $cancelationEndDate
            )
        }
      }
    `,
    pageContent: graphql`
      query stopRoutes_TerminalPageContent_Query($terminalId: String!) {
        station(id: $terminalId) {
          ...TerminalPageContentContainer_station
        }
      }
    `,
    pageTimetable: graphql`
      query stopRoutes_TerminalPageTimetable_Query(
        $terminalId: String!
        $date: String!
      ) {
        station(id: $terminalId) {
          ...TerminalTimetablePage_station @arguments(date: $date)
        }
      }
    `,
    pageAlerts: graphql`
      query stopRoutes_TerminalDisruptions_Query(
        $terminalId: String!
        $cancelationStartDate: OffsetDateTime!
        $cancelationEndDate: OffsetDateTime!
      ) {
        station(id: $terminalId) {
          ...DisruptionsFragment
            @arguments(
              cancelationStartDate: $cancelationStartDate
              cancelationEndDate: $cancelationEndDate
            )
        }
      }
    `,
  },
};

export default function getStopRoutes(isTerminal = false) {
  const renderTabContainer = createStickyRenderer();
  const queryMap = isTerminal ? queries.station : queries.stop;
  return (
    <Route path={`/${isTerminal ? PREFIX_TERMINALS : PREFIX_STOPS}`}>
      <Route Component={Error404} />
      {/* TODO: Should return list of all routes */}
      <Route path={isTerminal ? ':terminalId' : ':stopId'}>
        {{
          title: (
            <Route
              path="(.*)?"
              getComponent={() => {
                return isTerminal
                  ? import(
                      /* webpackChunkName: "stop" */ '../component/stop/TerminalTitle'
                    ).then(getDefault)
                  : import(
                      /* webpackChunkName: "stop" */ '../component/stop/StopTitle'
                    ).then(getDefault);
              }}
              render={getComponentOrNullRenderer}
            />
          ),
          header: (
            <Route
              path="(.*)?"
              getComponent={() => {
                return isTerminal
                  ? import(
                      /* webpackChunkName: "stop" */ '../component/stop/TerminalPageHeaderContainer'
                    ).then(getDefault)
                  : import(
                      /* webpackChunkName: "stop" */ '../component/stop/StopPageHeaderContainer'
                    ).then(getDefault);
              }}
              query={queryMap.pageHeader}
              render={getComponentOrNullRenderer}
            />
          ),
          content: (
            <Route
              getComponent={() => {
                return isTerminal
                  ? import(
                      /* webpackChunkName: "stop" */ '../component/stop/TerminalPageTabContainer'
                    ).then(getDefault)
                  : import(
                      /* webpackChunkName: "stop" */ '../component/stop/StopPageTabContainer'
                    ).then(getDefault);
              }}
              query={queryMap.pageTab}
              prepareVariables={prepareDatesForStopTabs}
              render={renderTabContainer}
            >
              <Route
                getComponent={() => {
                  return isTerminal
                    ? import(
                        /* webpackChunkName: "stop" */ '../component/stop/TerminalPageContentContainer'
                      )
                        .then(getDefault)
                        .catch(errorLoading)
                    : import(
                        /* webpackChunkName: "stop" */ '../component/stop/StopPageContentContainer'
                      )
                        .then(getDefault)
                        .catch(errorLoading);
                }}
                query={queryMap.pageContent}
                render={({ Component, props, error }) => {
                  if (Component && (props || error)) {
                    if (!error && !(props.stop || props.station)) {
                      throw new RedirectException(
                        `/${isTerminal ? PREFIX_TERMINALS : PREFIX_STOPS}`,
                      );
                    } else {
                      return <Component {...props} error={error} />;
                    }
                  }
                  return <Loading />;
                }}
              />
              <Route
                path={PREFIX_TIMETABLE}
                getComponent={() => {
                  return isTerminal
                    ? import(
                        /* webpackChunkName: "stop" */ '../component/stop/TerminalTimetablePage'
                      )
                        .then(getDefault)
                        .catch(errorLoading)
                    : import(
                        /* webpackChunkName: "stop" */ '../component/stop/StopTimetablePage'
                      )
                        .then(getDefault)
                        .catch(errorLoading);
                }}
                query={queryMap.pageTimetable}
                prepareVariables={(params, { location }) => {
                  const date = location?.query?.date;
                  return {
                    ...params,
                    date: date || DateTime.now().toFormat(DATE_FORMAT),
                  };
                }}
                render={getComponentOrLoadingRenderer}
              />
              <Route
                path={PREFIX_DISRUPTION}
                getComponent={() =>
                  import(
                    /* webpackChunkName: "stop" */ '../component/stop/Disruptions'
                  )
                    .then(getDefault)
                    .catch(errorLoading)
                }
                query={queryMap.pageAlerts}
                prepareVariables={prepareDatesForStops}
                render={getComponentOrNullRenderer}
              />
            </Route>
          ),
          map: (
            <Route
              path="(.*)?"
              getComponent={() => {
                // eslint-disable-next-line no-nested-ternary
                return isTerminal
                  ? import(
                      /* webpackChunkName: "stop" */ '../component/stop/TerminalPageMapContainer'
                    ).then(getDefault)
                  : import(
                      /* webpackChunkName: "stop" */ '../component/stop/StopPageMapContainer'
                    ).then(getDefault);
              }}
              query={queryMap.pageMap}
              render={getComponentOrNullRenderer}
            />
          ),
          meta: (
            <Route
              path="(.*)?"
              getComponent={() => {
                return isTerminal
                  ? import(
                      /* webpackChunkName: "stop" */ '../component/stop/TerminalPageMeta'
                    ).then(getDefault)
                  : import(
                      /* webpackChunkName: "stop" */ '../component/stop/StopPageMeta'
                    ).then(getDefault);
              }}
              query={queryMap.pageMeta}
              render={getComponentOrNullRenderer}
            />
          ),
        }}
      </Route>
    </Route>
  );
}
